import { useContext, useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ChevronRight } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';

import { MenuTriggerContext } from '@components/primitives/Menu/context';
import { easeOutSmooth } from '@components/primitives/utilities';

import styles from './styles.module.css';

type MenuSubMenuProps = DropdownMenu.DropdownMenuSubContentProps & {
  label: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  disabled?: boolean;
};

export function MenuSubMenu({
  label,
  icon,
  children,
  disabled,
  ...props
}: MenuSubMenuProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useContext(MenuTriggerContext);
  return (
    <DropdownMenu.Sub open={open} onOpenChange={setOpen}>
      <DropdownMenu.SubTrigger className={styles.menuItem} disabled={disabled}>
        {icon && (
          <span className={styles.icon} aria-hidden>
            {icon}
          </span>
        )}
        <span className={styles.label}>{label}</span>
        <ChevronRight className={styles.subMenuArrow} aria-hidden />
      </DropdownMenu.SubTrigger>

      <AnimatePresence>
        {open && (
          <DropdownMenu.Portal
            forceMount
            container={triggerRef?.current?.closest("[role='dialog']")}
          >
            <DropdownMenu.SubContent
              className={styles.menu}
              sideOffset={3}
              alignOffset={-2}
              {...props}
              asChild
            >
              <motion.div {...ANIMATIONS}>{children}</motion.div>
            </DropdownMenu.SubContent>
          </DropdownMenu.Portal>
        )}
      </AnimatePresence>
    </DropdownMenu.Sub>
  );
}

const ANIMATIONS = {
  initial: {
    opacity: 0,
    x: -2,
    scale: 0.97,
  },
  animate: {
    opacity: 1,
    x: 0,
    scale: 1,
  },
  exit: {
    opacity: 0,
    x: -2,
    scale: 0.97,
    transition: {
      duration: 0.1,
      ease: easeOutSmooth,
    },
  },
  transition: {
    duration: 0.2,
    ease: easeOutSmooth,
  },
};
